import { useState } from 'react'
import { ListItem } from '@/components/ListItem'

/* ---------- helpers ---------- */

function Section ({
	title,
	children
}: {
	title: string
	children: React.ReactNode
}) {
	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-16'>
			<h3 className='spaces:text-14 spaces:font-semi spaces:text-content-3 spaces:uppercase spaces:tracking-wide'>
				{title}
			</h3>
			{children}
		</div>
	)
}

function ListBox ({ children }: { children: React.ReactNode }) {
	return (
		<div className='spaces:flex spaces:flex-col spaces:w-[400px] spaces:rounded-8 spaces:border spaces:border-canvas-3 spaces:overflow-hidden spaces:bg-canvas-base spaces:p-4'>
			{children}
		</div>
	)
}

/* ---------- mock data ---------- */

const BEDROOM_OPTIONS = [
	{ value: 'studio', label: 'Studio', secondaryLabel: '4' },
	{ value: '1br', label: '1 Bedroom', secondaryLabel: '12' },
	{ value: '2br', label: '2 Bedrooms', secondaryLabel: '8' },
	{ value: '3br', label: '3 Bedrooms', secondaryLabel: '3' },
	{ value: '4br', label: '4 Bedrooms', secondaryLabel: '0' }
]

const FLOOR_OPTIONS = [
	{ value: 'low', label: 'Low (1–3)' },
	{ value: 'mid', label: 'Mid (4–7)' },
	{ value: 'high', label: 'High (8+)' },
	{ value: 'penthouse', label: 'Penthouse' }
]

/* ---------- sandbox ---------- */

export function ListItemSandbox () {
	/* check indicator (single-select) */
	const [selectedFloor, setSelectedFloor] = useState<string | null>('mid')

	/* checkbox indicator (multi-select) */
	const [selectedBeds, setSelectedBeds] = useState<string[]>(['studio', '2br'])

	const toggleBed = (value: string) => {
		setSelectedBeds(prev =>
			prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
		)
	}

	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-32'>
			{/* ── Check indicator (single-select) ── */}
			<Section title='Check Indicator — Single Select'>
				<ListBox>
					{FLOOR_OPTIONS.map(opt => (
						<ListItem
							key={opt.value}
							label={opt.label}
							selectionIndicator='check'
							isSelected={selectedFloor === opt.value}
							onToggle={() =>
								setSelectedFloor(prev => (prev === opt.value ? null : opt.value))
							}
						/>
					))}
				</ListBox>
			</Section>

			{/* ── Checkbox indicator (multi-select) ── */}
			<Section title='Checkbox Indicator — Multi Select'>
				<ListBox>
					{BEDROOM_OPTIONS.map(opt => (
						<ListItem
							key={opt.value}
							label={opt.label}
							secondaryLabel={opt.secondaryLabel}
							selectionIndicator='checkbox'
							isSelected={selectedBeds.includes(opt.value)}
							onToggle={() => toggleBed(opt.value)}
						/>
					))}
				</ListBox>
			</Section>

			{/* ── With icons ── */}
			<Section title='With Icons'>
				<ListBox>
					<ListItem
						label='City View'
						iconLeft='Map'
						selectionIndicator='check'
						isSelected={false}
					/>
					<ListItem
						label='Park View'
						iconLeft='Sun'
						selectionIndicator='check'
						isSelected={true}
					/>
					<ListItem
						label='Water View'
						iconLeft='EyeOpen'
						selectionIndicator='check'
						isSelected={false}
					/>
					<ListItem
						label='Details'
						iconRight='CaretRight'
						selectionIndicator='check'
					/>
				</ListBox>
			</Section>

			{/* ── Long label (wrapping) ── */}
			<Section title='Long Label — Wrapping'>
				<ListBox>
					<ListItem
						label='South-facing corner unit with panoramic city and water views'
						selectionIndicator='check'
						isSelected={false}
					/>
					<ListItem
						label='Top-floor penthouse with private rooftop terrace access'
						selectionIndicator='check'
						isSelected={true}
					/>
				</ListBox>
			</Section>

			{/* ── Disabled state ── */}
			<Section title='Disabled State'>
				<ListBox>
					<ListItem
						label='Available option'
						selectionIndicator='check'
						isSelected={false}
					/>
					<ListItem
						label='Disabled option'
						selectionIndicator='check'
						isSelected={false}
						isDisabled={true}
					/>
					<ListItem
						label='Disabled + selected'
						selectionIndicator='check'
						isSelected={true}
						isDisabled={true}
					/>
					<ListItem
						label='Disabled checkbox'
						secondaryLabel='0'
						selectionIndicator='checkbox'
						isSelected={false}
						isDisabled={true}
					/>
				</ListBox>
			</Section>
		</div>
	)
}
